import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Button,
  IconButton,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { MdArrowBack, MdRefresh } from 'react-icons/md'
import { LuScan } from 'react-icons/lu'
import { useState } from 'react'
import DicomUploadZone from './DicomUploadZone'
import DicomViewer from './DicomViewer'
import RadiologyResultsPanel from './RadiologyResultsPanel'
import NotesPanel from '../SpecialtyWorkspace/NotesPanel'
import CasesList from '../SpecialtyWorkspace/CasesList'

export default function RadiologyWorkspace() {
  const navigate = useNavigate()
  const toast = useToast()
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [dicomImages, setDicomImages] = useState<string[]>([])
  const [analyzing, setAnalyzing] = useState(false)
  const [hasResults, setHasResults] = useState(false)

  const handleFilesUploaded = (files: File[]) => {
    setUploadedFiles(files)

    // Simulate loading DICOM images
    const imageUrls = files.map(file => URL.createObjectURL(file))
    setDicomImages(imageUrls)

    // Simulate AI analysis
    setAnalyzing(true)
    setTimeout(() => {
      setAnalyzing(false)
      setHasResults(true)
      toast({
        title: 'Analysis Complete',
        description: 'DICOM series analyzed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    }, 2500)
  }

  const handleNewAnalysis = () => {
    setUploadedFiles([])
    setDicomImages([])
    setHasResults(false)
    setAnalyzing(false)
  }

  return (
    <VStack spacing={4} align="stretch" h="100%">
      {/* Header */}
      <Box
        bgGradient="linear(135deg, blue.600 0%, blue.500 50%, cyan.400 100%)"
        borderRadius="2xl"
        p={6}
        position="relative"
        overflow="hidden"
        boxShadow="2xl"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      >
        <HStack justify="space-between" position="relative" zIndex={1}>
          <HStack spacing={4}>
            <IconButton
              aria-label="Back to Lab"
              icon={<MdArrowBack />}
              onClick={() => navigate('/lab')}
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
              size="lg"
            />
            <Box
              p={3}
              bg="whiteAlpha.300"
              backdropFilter="blur(10px)"
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.400"
            >
              <Icon as={LuScan} boxSize={8} color="white" />
            </Box>
            <VStack spacing={1} align="start">
              <Heading size="lg" color="white" fontWeight="bold" letterSpacing="tight">
                Radiology Analysis
              </Heading>
              <HStack spacing={1.5}>
                <Badge colorScheme="green" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                  AI-Powered
                </Badge>
                <Badge colorScheme="orange" variant="solid" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                  BETA
                </Badge>
              </HStack>
            </VStack>
          </HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={handleNewAnalysis}
            variant="solid"
            bg="whiteAlpha.300"
            color="white"
            _hover={{ bg: 'whiteAlpha.400', transform: 'rotate(180deg)' }}
            transition="all 0.3s"
            size="lg"
            borderRadius="xl"
          >
            New Analysis
          </Button>
        </HStack>
      </Box>

      {/* Main Workspace Grid */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 400px' }}
        gap={4}
        flex={1}
        overflow="hidden"
      >
        {/* Left Side - DICOM Viewer */}
        <GridItem>
          <Tabs
            variant="enclosed"
            colorScheme="blue"
            h="100%"
            display="flex"
            flexDirection="column"
          >
            <TabList>
              <Tab>DICOM Viewer</Tab>
              <Tab>Upload Files</Tab>
              <Tab>Recent Cases</Tab>
            </TabList>

            <TabPanels flex={1} overflow="auto">
              <TabPanel h="100%">
                <DicomViewer
                  dicomImages={dicomImages}
                  analyzing={analyzing}
                  hasFiles={uploadedFiles.length > 0}
                />
              </TabPanel>
              <TabPanel h="100%">
                <DicomUploadZone
                  onFilesUploaded={handleFilesUploaded}
                  uploadedFiles={uploadedFiles}
                  analyzing={analyzing}
                />
              </TabPanel>
              <TabPanel>
                <CasesList specialtyId="radiology" />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>

        {/* Right Side - Results & Notes */}
        <GridItem>
          <Tabs
            variant="enclosed"
            colorScheme="blue"
            h="100%"
            display="flex"
            flexDirection="column"
          >
            <TabList>
              <Tab>AI Results</Tab>
              <Tab>Notes</Tab>
            </TabList>

            <TabPanels flex={1} overflow="auto">
              <TabPanel h="100%">
                <RadiologyResultsPanel
                  hasResults={hasResults}
                  analyzing={analyzing}
                />
              </TabPanel>
              <TabPanel h="100%">
                <NotesPanel />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>
      </Grid>
    </VStack>
  )
}
